import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Icon from '@mui/material/Icon';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import { alpha, useTheme as useMuiTheme } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

import {
  buildMemberAnalysis,
  hm,
  memberName,
  moneyTry,
  statusIcon,
  statusLabel,
} from './memberReportUtils';

function Surface({ children, sx = {} }) {
  const theme = useMuiTheme();
  return (
    <MDBox
      sx={{
        borderRadius: '22px',
        border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
        background: `linear-gradient(180deg, ${alpha(
          theme.palette.background.paper,
          0.98
        )}, ${alpha(theme.palette.info.main, 0.03)})`,
        ...sx,
      }}
    >
      {children}
    </MDBox>
  );
}

function PackageLane({ group, compact = false }) {
  const theme = useMuiTheme();
  return (
    <Surface sx={{ p: compact ? 1.2 : 1.5, display: 'grid', gap: 1 }}>
      <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1}>
        <MDBox sx={{ minWidth: 0 }}>
          <MDTypography fontWeight="bold" sx={{ lineHeight: 1.1 }} noWrap>
            {group.planName}
          </MDTypography>
          <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
            Kullanım {group.usedCount}
            {group.total ? ` / ${group.total}` : ''} • Paket #{group.memberPlanId || '—'}
          </MDTypography>
        </MDBox>
        <Chip
          size="small"
          label={`${group.items.length} kayıt`}
          sx={{ borderRadius: '999px', fontWeight: 800 }}
        />
      </MDBox>

      <MDBox sx={{ display: 'flex', gap: 0.8, overflowX: 'auto', pb: 0.2 }}>
        {group.items.map((item) => {
          const attended = String(item.attendanceStatus || '').toUpperCase() === 'ATTENDED';
          const negative = ['NO_SHOW', 'BURNED', 'CANCELLED'].includes(
            String(item.attendanceStatus || '').toUpperCase()
          );

          return (
            <MDBox
              key={`${group.key}-${item.id}`}
              sx={{
                minWidth: compact ? 176 : 210,
                p: compact ? 1 : 1.1,
                borderRadius: '16px',
                border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
                bgcolor: attended
                  ? alpha(theme.palette.success.main, 0.08)
                  : negative
                  ? alpha(theme.palette.error.main, 0.08)
                  : alpha(theme.palette.info.main, 0.06),
                display: 'grid',
                gap: 0.4,
              }}
            >
              <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                <MDTypography variant="caption" fontWeight="bold">
                  {item.dayISO || '—'} • {hm(item.startMin)}
                </MDTypography>
                <Icon
                  sx={{
                    fontSize: 18,
                    color: attended
                      ? theme.palette.success.main
                      : negative
                      ? theme.palette.error.main
                      : theme.palette.info.main,
                  }}
                >
                  {statusIcon(item.attendanceStatus)}
                </Icon>
              </MDBox>

              <MDTypography variant="button" fontWeight="bold" sx={{ lineHeight: 1.15 }}>
                {item.activityName || 'Seans'}
              </MDTypography>
              <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
                Eğitmen: {item.trainerName || '—'}
              </MDTypography>
              <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
                Durum: {statusLabel(item.attendanceStatus)}
              </MDTypography>
              <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
                Paket sırası: {item.usageIndex || '—'}
                {item.packageTotal ? ` / ${item.packageTotal}` : ''}
              </MDTypography>
            </MDBox>
          );
        })}
      </MDBox>
    </Surface>
  );
}

export function MemberAttendanceFlowModal({ open, onClose, member, groups }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ p: 2 }}>
        <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1}>
          <MDBox>
            <MDTypography variant="h6" fontWeight="bold">
              Yoklama Akışı
            </MDTypography>
            <MDTypography variant="button" sx={{ color: 'text.secondary' }}>
              {memberName(member)} için paket bağlamında yoklama ve kullanım akışı
            </MDTypography>
          </MDBox>
          <IconButton onClick={onClose}>
            <Icon>close</Icon>
          </IconButton>
        </MDBox>
      </DialogTitle>

      <DialogContent sx={{ pt: 0, pb: 2 }}>
        <MDBox sx={{ display: 'grid', gap: 1.2 }}>
          {(groups || []).length === 0 ? (
            <Surface sx={{ p: 2 }}>
              <MDTypography variant="button" sx={{ color: 'text.secondary' }}>
                Bu üyede gösterilecek paket veya yoklama akışı bulunmuyor.
              </MDTypography>
            </Surface>
          ) : null}
          {(groups || []).map((group) => (
            <PackageLane key={group.key} group={group} />
          ))}
        </MDBox>
      </DialogContent>
    </Dialog>
  );
}

export function MemberAnalysisModal({ open, onClose, member, groups }) {
  const theme = useMuiTheme();
  const analysis = React.useMemo(() => buildMemberAnalysis(member, groups || []), [member, groups]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ p: 2 }}>
        <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1}>
          <MDBox>
            <MDTypography variant="h6" fontWeight="bold">
              Üye Analizi
            </MDTypography>
            <MDTypography variant="button" sx={{ color: 'text.secondary' }}>
              {analysis.memberName} için hızlı davranış ve kullanım özeti
            </MDTypography>
          </MDBox>
          <IconButton onClick={onClose}>
            <Icon>close</Icon>
          </IconButton>
        </MDBox>
      </DialogTitle>

      <DialogContent sx={{ pt: 0, pb: 2 }}>
        <MDBox
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
            gap: 1,
            mb: 1.2,
          }}
        >
          {[
            ['Aktif Borç', moneyTry(analysis.openDebtTry), 'warning'],
            ['Yoklama', String(analysis.attendanceCount), 'info'],
            ['Katıldı', String(analysis.attended), 'success'],
            ['Gelmedi / İptal', `${analysis.noShow + analysis.cancelled}`, 'error'],
          ].map(([label, value, tone]) => (
            <Surface key={label} sx={{ p: 1.3 }}>
              <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
                {label}
              </MDTypography>
              <MDTypography variant="h6" fontWeight="bold">
                {value}
              </MDTypography>
              <MDBox
                sx={{
                  mt: 0.7,
                  width: 42,
                  height: 4,
                  borderRadius: '999px',
                  bgcolor:
                    tone === 'success'
                      ? alpha(theme.palette.success.main, 0.8)
                      : tone === 'warning'
                      ? alpha(theme.palette.warning.main, 0.8)
                      : tone === 'error'
                      ? alpha(theme.palette.error.main, 0.8)
                      : alpha(theme.palette.info.main, 0.8),
                }}
              />
            </Surface>
          ))}
        </MDBox>

        <Surface sx={{ p: 1.5, mb: 1.2 }}>
          <MDTypography fontWeight="bold">Çalıştığı Eğitmenler</MDTypography>
          <MDBox sx={{ display: 'flex', gap: 0.7, flexWrap: 'wrap', mt: 1 }}>
            {analysis.trainers.length ? (
              analysis.trainers.map((trainer) => (
                <Chip
                  key={trainer}
                  label={trainer}
                  variant="outlined"
                  sx={{ borderRadius: '999px', fontWeight: 700 }}
                />
              ))
            ) : (
              <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
                Eğitmen verisi henüz oluşmamış.
              </MDTypography>
            )}
          </MDBox>
        </Surface>

        <MDTypography fontWeight="bold" sx={{ mb: 1 }}>
          Son Paket Akışı
        </MDTypography>
        <MDBox sx={{ display: 'grid', gap: 1 }}>
          {analysis.latestPackages.length ? (
            analysis.latestPackages.map((group) => (
              <PackageLane key={group.key} group={group} compact />
            ))
          ) : (
            <Surface sx={{ p: 1.6 }}>
              <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
                Son paket akışı bulunmuyor.
              </MDTypography>
            </Surface>
          )}
        </MDBox>
      </DialogContent>
    </Dialog>
  );
}
